/* eslint-disable no-unused-vars */
import * as Sentry from '@sentry/react'
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
  getAdditionalUserInfo,
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  User,
} from 'firebase/auth'
import { auth, googleProvider } from '@/lib/firebase'
import {
  requestUserSubscription,
  isSubscriptionActive,
  UserSubscription,
} from '@/services/subscription'
import { validatePromoCode, decrementPromoCodeUses } from '@/services/promoCode'

interface AuthContextType {
  user: User | null
  loading: boolean
  subscription: UserSubscription | null
  subscriptionRequired: boolean | null
  hasActiveSubscription: boolean | null
  subscriptionLoading: boolean
  subscriptionError: string | null
  googleSignIn: (promoCode?: string, skipPromoGate?: boolean) => Promise<void>
  googleSignInPending: boolean
  consumeFreshSignup: (uid: string) => boolean
  logout: (destination?: 'signin') => Promise<void>
  refreshSubscription: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  // A missing provider used to surface later as a confusing null access in
  // whichever field the caller read first. Say what is wrong where it happens.
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.')
  return context
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [subscription, setSubscription] = useState<UserSubscription | null>(null)
  const [subscriptionRequired, setSubscriptionRequired] = useState<boolean | null>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null)
  const subscriptionRequest = useRef<AbortController | null>(null)
  // V1's uid-keyed page tree remounts before the popup returns its signup result.
  // Keep this handoff above that tree, bound to the account that just signed up.
  const [googleSignInPending, setGoogleSignInPending] = useState(false)
  const freshSignupUid = useRef<string | null>(null)
  const consumeFreshSignup = useCallback((uid: string) => {
    if (freshSignupUid.current !== uid) return false
    freshSignupUid.current = null
    return true
  }, [])

  const loadSubscription = useCallback(async (uid?: string) => {
    subscriptionRequest.current?.abort()
    const controller = new AbortController()
    subscriptionRequest.current = controller
    setSubscriptionError(null)
    if (!uid) {
      setSubscription(null)
      setSubscriptionRequired(null)
      setHasActiveSubscription(false)
      setSubscriptionLoading(false)
      return
    }

    setSubscriptionLoading(true)
    try {
      const result = await requestUserSubscription(uid, controller.signal)
      if (controller.signal.aborted || auth.currentUser?.uid !== uid) return
      setSubscription(result.subscription)
      setSubscriptionRequired(result.subscriptionRequired)
      setHasActiveSubscription(isSubscriptionActive(result.subscription))
    } catch (error) {
      if (controller.signal.aborted || auth.currentUser?.uid !== uid) return
      console.error('Error refreshing subscription:', error)
      setSubscription(null)
      setSubscriptionRequired(null)
      setHasActiveSubscription(false)
      setSubscriptionError('We could not check your subscription. Please retry before continuing.')
    } finally {
      if (!controller.signal.aborted && auth.currentUser?.uid === uid) {
        setSubscriptionLoading(false)
      }
    }
  }, [])

  const refreshSubscription = useCallback(
    () => loadSubscription(auth.currentUser?.uid),
    [loadSubscription]
  )

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      if (!u) freshSignupUid.current = null
      setUser(u)
      setSubscription(null)
      setSubscriptionRequired(null)
      setHasActiveSubscription(null)
      setLoading(false)
      void loadSubscription(u?.uid)

      if (u) {
        Sentry.setUser({ id: u.uid, email: u.email ?? undefined })
      } else {
        Sentry.setUser(null)
      }
    })

    return () => {
      unsubscribe()
      subscriptionRequest.current?.abort()
    }
  }, [loadSubscription])

  /** V1 explicitly opts out of the legacy beta promo-code requirement. */
  const googleSignIn = useCallback(async (promoCode?: string, skipPromoGate = false) => {
    freshSignupUid.current = null
    setGoogleSignInPending(true)
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const isNewUser =
        getAdditionalUserInfo(result)?.isNewUser ??
        result.user.metadata.creationTime === result.user.metadata.lastSignInTime

      if (isNewUser && !skipPromoGate) {
        if (!promoCode) {
          await signOut(auth)
          window.location.href = '/?registerError=promoMissing'
          return
        }

        const validation = await validatePromoCode(promoCode)
        if (!validation.isValid) {
          await signOut(auth)
          const errorParam = encodeURIComponent(validation.error || 'Promo code is invalid')
          window.location.href = `/?registerError=promoInvalid&message=${errorParam}`
          return
        }

        try {
          await decrementPromoCodeUses(promoCode)
        } catch (error) {
          console.error('Failed to use promo code:', error)
          await signOut(auth)
          window.location.href = '/?registerError=promoUsageFailed'
          return
        }
      }

      if (isNewUser) freshSignupUid.current = result.user.uid
    } finally {
      setGoogleSignInPending(false)
    }
  }, [])

  const logout = useCallback(async (destination?: 'signin') => {
    freshSignupUid.current = null
    await signOut(auth)
    // Clear all persisted state from sessionStorage
    sessionStorage.clear()
    // Navigate only after sign-out, without first reloading the billing page.
    if (destination === 'signin') {
      window.location.assign('/v1/signin')
    } else {
      window.location.reload()
    }
  }, [])

  const subscriptionStatus = subscription?.status
  const subscriptionPeriodEnd = subscription?.currentPeriodEnd

  useEffect(() => {
    if (!user || !subscriptionStatus) return

    const periodEndMs = subscriptionPeriodEnd
      ? new Date(subscriptionPeriodEnd).getTime()
      : undefined

    const trialStillActive =
      subscriptionStatus === 'trialing' && (periodEndMs === undefined || periodEndMs > Date.now())

    if (trialStillActive) return
    localStorage.removeItem(`trial_started_${user.uid}`)
  }, [user, subscriptionStatus, subscriptionPeriodEnd])

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      subscription,
      subscriptionRequired,
      hasActiveSubscription,
      subscriptionLoading,
      subscriptionError,
      googleSignIn,
      googleSignInPending,
      consumeFreshSignup,
      logout,
      refreshSubscription,
    }),
    [
      user,
      loading,
      subscription,
      subscriptionRequired,
      hasActiveSubscription,
      subscriptionLoading,
      subscriptionError,
      googleSignIn,
      googleSignInPending,
      consumeFreshSignup,
      logout,
      refreshSubscription,
    ]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
